import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './EditableField.css';

/*
 * EditableField - Inline editable text component
 *
 * Props:
 *   storageKey: unique key for localStorage persistence
 *   defaultValue: the default text (from code)
 *   tag: HTML tag to render ('p', 'h1', 'h2', 'h3', 'span', etc.) - default 'p'
 *   className: CSS class for the rendered element
 *   multiline: if true, uses a textarea instead of input
 */

function EditableField({ storageKey, defaultValue, tag: Tag = 'p', className = '', multiline = false, style }) {
  const { isLoggedIn } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem(`editable_${storageKey}`);
    setValue(stored !== null ? stored : defaultValue);
  }, [storageKey, defaultValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (multiline) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      }
    }
  }, [isEditing, multiline]);

  const handleSave = () => {
    localStorage.setItem(`editable_${storageKey}`, value);
    setIsEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const handleCancel = () => {
    const stored = localStorage.getItem(`editable_${storageKey}`);
    setValue(stored !== null ? stored : defaultValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleReset = () => {
    localStorage.removeItem(`editable_${storageKey}`);
    setValue(defaultValue);
    setIsEditing(false);
  };

  if (isEditing && isLoggedIn) {
    return (
      <div className="editable-field__editing">
        {multiline ? (
          <textarea
            ref={inputRef}
            className="editable-field__textarea"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <input
            ref={inputRef}
            className="editable-field__input"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        )}
        <div className="editable-field__actions">
          <button className="editable-field__btn editable-field__btn--save" onClick={handleSave}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Save
          </button>
          <button className="editable-field__btn editable-field__btn--cancel" onClick={handleCancel}>Cancel</button>
          <button className="editable-field__btn editable-field__btn--reset" onClick={handleReset}>Reset</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`editable-field__wrapper ${isLoggedIn ? 'editable-field__wrapper--active' : ''}`}>
      <Tag className={className} style={style}>
        {value}
      </Tag>
      {isLoggedIn && (
        <button
          className={`editable-field__edit-btn ${saved ? 'editable-field__edit-btn--saved' : ''}`}
          onClick={() => setIsEditing(true)}
          title="Edit this text"
        >
          {saved ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

export default EditableField;
